import axios from 'axios';
import { cloneDeep } from 'lodash';
import { StatusCodes } from 'http-status-codes';
import { baseApiUrl } from '@/config';
import { saveState, getSavedState, removeSavedState } from '@/helpers/storageOperations';
import { SYS_ADMIN_ROLE, ADMIN_ROLE, AGENT_ROLE, GUEST_ROLE } from '@/assets/common/roles';
import getSharedLinkCode from '@/helpers/dashboard/getSharedLinkCode';

export const state = () => ({
    currentUser: getSavedState('auth.currentUser') || {},
    csrfToken: getSavedState('auth.csrfToken') || null,
    accessToken: getSavedState('auth.accessToken') || null,
    refreshToken: getSavedState('auth.refreshToken') || null,
    refreshTokenPromise: undefined,
    takeTokenPromise: undefined,
    isAccountOvertaken: getSavedState('auth.isAccountOvertaken') || false,
    isTwoFactorAuthEnabled: false,
    ALL_ROLES: [SYS_ADMIN_ROLE, ADMIN_ROLE, AGENT_ROLE, GUEST_ROLE],
    SYS_ADMIN_ROLE,
    ADMIN_ROLE,
    AGENT_ROLE,
    GUEST_ROLE
});

export const getters = {
    ALL_ROLES: state => state.ALL_ROLES,
    SYS_ADMIN_ROLE: state => state.SYS_ADMIN_ROLE,
    ADMIN_ROLE: state => state.ADMIN_ROLE,
    AGENT_ROLE: state => state.AGENT_ROLE,
    GUEST_ROLE: state => state.GUEST_ROLE,

    loggedUser: state => state.currentUser,
    isLoggedIn: state => !!state.currentUser.id,
    isMcpUser: state => !!state.currentUser?.isMcpUser,
    loggedUserRole: (state, { loggedUser }) => loggedUser.role,
    isSysAdmin: ({ SYS_ADMIN_ROLE }, { loggedUserRole }) => loggedUserRole === SYS_ADMIN_ROLE,
    isAdmin: ({ ADMIN_ROLE }, { loggedUserRole }) => loggedUserRole === ADMIN_ROLE,
    isAgent: ({ AGENT_ROLE }, { loggedUserRole }) => loggedUserRole === AGENT_ROLE,
    isGuest: ({ GUEST_ROLE }, { loggedUserRole }) => loggedUserRole === GUEST_ROLE,
    isTwoFactorAuthEnabled: state => state.isTwoFactorAuthEnabled,

    isAccountOvertaken: state => state.isAccountOvertaken,
    refreshToken: state => state.refreshToken,
    refreshTokenPromise: state => state.refreshTokenPromise,
    takeTokenPromise: state => state.takeTokenPromise,
    permissions: state => state.currentUser?.permissions
};

export const mutations = {
    SET_CURRENT_USER(state, newValue) {
        state.currentUser = newValue;

        if (getSharedLinkCode() === null) {
            saveState('auth.currentUser', newValue);
        }
    },

    CLEAR_USER(state) {
        state.currentUser = {};
        removeSavedState('auth.currentUser');
    },

    SET_TWO_FACTOR_AUTH(state, isEnabled) {
        state.isTwoFactorAuthEnabled = isEnabled;

        saveState('auth.isTwoFactorAuthEnabled', isEnabled);
    },

    SET_CSRF_TOKEN(state, csrfToken) {
        state.csrfToken = csrfToken;

        saveState('auth.csrfToken', csrfToken);
    },

    SET_ACCESS_TOKEN(state, accessToken) {
        state.accessToken = accessToken;

        saveState('auth.accessToken', accessToken);
    },

    SET_REFRESH_TOKEN(state, refreshToken) {
        state.refreshToken = refreshToken;

        saveState('auth.refreshToken', refreshToken);
    },

    SET_INVESTIGATING_ANOTHER_ACCOUNT(state, isAccountOvertaken) {
        state.isAccountOvertaken = isAccountOvertaken;

        saveState('auth.isAccountOvertaken', isAccountOvertaken);
    },

    SET_DEFAULT_STREAMS(state, defaultStreams) {
        state.currentUser.preferences.ui.defaultDashboardStreams = defaultStreams;
        if (getSharedLinkCode() === null) {
            saveState('auth.currentUser', state.currentUser);
        }
    },

    SET_REFRESH_TOKEN_PROMISE(state, refreshTokenPromise) {
        state.refreshTokenPromise = refreshTokenPromise;
    },

    SET_TAKE_TOKEN_PROMISE(state, takeTokenPromise) {
        state.takeTokenPromise = takeTokenPromise;
    },
};

export const actions = {
    init({ dispatch }) {
        dispatch('validate');
    },

    async login({ dispatch }, { email, password, otpCode } = {}) {
        const payload = {
            email,
            password
        };

        if (otpCode) {
            payload.otp_code = otpCode;
        }

        const { account: user, csrfToken, accessToken, refreshToken } = await axios.$post('/v1/session', payload);

        dispatch('saveAuthState', {
            user,
            csrfToken,
            accessToken,
            refreshToken,
            isAccountOvertaken: false
        });

        return user;
    },

    getFlashMessages() {
        return axios.$get(`${baseApiUrl}/flash_messages`);
    },

    async loginWithCode({ dispatch }, code) {
        const { account: user, csrfToken, accessToken, refreshToken } = await axios.$post('/v1/session', { auth_code: code });

        dispatch('saveAuthState', {
            user,
            csrfToken,
            accessToken,
            refreshToken,
            isAccountOvertaken: false
        });

        return user;
    },

    async refreshToken({ commit, dispatch, getters }) {
        if (getters.refreshTokenPromise === undefined) {
            const promise = new Promise(
                (resolve, reject) => {
                    axios
                        .$put(
                            '/v1/session',
                            {check_old_ui_authorization: true},
                            {headers: {Authorization: `Bearer ${getters.refreshToken}`}}
                        )
                        .then(({ csrfToken, accessToken, refreshToken }) => {
                            dispatch('saveAuthState', {csrfToken, accessToken, refreshToken});
                            resolve({ csrfToken, accessToken });
                        })
                        .catch((e) => reject(e))
                        .finally(() => commit('SET_REFRESH_TOKEN_PROMISE', undefined));
            });

            commit('SET_REFRESH_TOKEN_PROMISE', promise);
        }

        return getters.refreshTokenPromise;
    },

    signUp(vuexContext, { firstName, lastName, email, password, passwordConfirmation, token } = {}) {
        const payload = {
            account: {
                first_name: firstName,
                last_name: lastName,
                email,
                password,
                password_confirmation: passwordConfirmation
            },
            team_member_invite_token: token
        };

        return axios.$post('/v1/accounts', payload);
    },

    async validateRegisterToken(vuexContext, token) {
        const { email } = await axios.$get('/v1/accounts/new', {
            params: {
                team_member_invite_token: token
            }
        });

        return email;
    },

    // @TODO check if we need to store it in the Vuex
    setTwoFactorAuth({ commit }, isEnabled) {
        commit('SET_TWO_FACTOR_AUTH', isEnabled);
    },

    async logout({ commit, dispatch }) {
        await axios.$delete('/v1/session');

        commit('SET_CURRENT_USER', {});

        dispatch('saveAuthState', {
            user: {},
            csrfToken: null,
            accessToken: null,
            refreshToken: null,
            isAccountOvertaken: false
        });

        saveState('cookies', {});

        //@TODO - bakcend have to standarise routes
        axios.$get(`${baseApiUrl}/accounts/logout`);
    },

    async validate({ dispatch, state }) {
        let { id } = state.currentUser;

        const sharedLinkCode = getSharedLinkCode();
        if (sharedLinkCode) {
            try {
                const link = await dispatch(`dashboard/sharedLinks/loadCurrentLink`, {code: sharedLinkCode}, {root: true});
                id = link.accountId;
            } catch (error) {
                return Promise.reject(error);
            }
        }

        if (!id) {
            try {
                //@TODO - bakcend have to standarise routes
                const userData = await axios.$get(`${baseApiUrl}/accounts/token`);
                dispatch('saveAuthState', userData);

                return userData.account;
            } catch (error) {
                if (error.response && error.response.status === StatusCodes.UNAUTHORIZED) {
                    dispatch('saveAuthState', {
                        user: {},
                        csrfToken: null,
                        accessToken: null,
                        refreshToken: null,
                        isAccountOvertaken: false
                    });
                }
                return Promise.reject(error)
            }
        }

        try {
            const user = await axios.$get(`/v1/accounts/${id}`);

            dispatch('saveAuthState', {
                user
            });

            return user;
        } catch (error) {
            if (error.response && error.response.status === StatusCodes.UNAUTHORIZED) {
                dispatch('saveAuthState', {
                    user: {},
                    csrfToken: null,
                    accessToken: null,
                    refreshToken: null,
                    isAccountOvertaken: false
                });
            }
            return Promise.reject(error)

        }
    },

    sendResetPasswordLink(vuexContext, email) {
        return axios.post('/v1/accounts/preferences/security/password_reset', { email });
    },

    validateResetPasswordToken(vuexContext, token) {
        return axios.$get('/v1/accounts/preferences/security/password_reset', { params: { token } });
    },

    resetPassword(vuexContext, { password, passwordConfirmation, token } = {}) {
        return axios.$put('/v1/accounts/preferences/security/password_reset', {
            account: {
                password,
                password_confirmation: passwordConfirmation
            },
            token
        });
    },

    async takeOver({ dispatch, commit }, accountId) {
        const { id } = await axios.$get(`/v1/admin/accounts/${accountId}`);

        if (id) {
            dispatch('saveAuthState', {
                isAccountOvertaken: true
            });
            commit('CLEAR_USER')

            saveState('cookies', { accountId: id });
        }
    },

    async takeOverBp({ dispatch }, accountId) {
        const {
            defaultAccount: { id }
        } = await axios.$get(`/v1/admin/business_profiles/${accountId}`);

        if (id) {
            dispatch('saveAuthState', {
                isAccountOvertaken: true
            });

            saveState('cookies', { accountId: id });

            const userData = await axios.$get(`${baseApiUrl}/accounts/token`);

            dispatch('saveAuthState', userData);

            return userData;
        }
    },

    async takeToken({ commit, dispatch, getters }) {
        if (getters.takeTokenPromise === undefined) {
            const promise = new Promise(
                (resolve, reject) => {
                    axios
                        .$get(`${baseApiUrl}/accounts/token`)
                        .then((userData) => {
                            dispatch('saveAuthState', userData);
                            resolve(userData);
                        })
                        .catch((e) => reject(e))
                        .finally(() => commit('SET_TAKE_TOKEN_PROMISE', undefined));
                });

            commit('SET_TAKE_TOKEN_PROMISE', promise);
        }

        return getters.takeTokenPromise;
    },

    async exitTakeOver({ dispatch, commit }) {
        const { cookies } = await axios.$delete('/v1/admin/accounts/takeover');

        saveState('cookies', cookies);
        dispatch('saveAuthState', {
            isAccountOvertaken: false
        });


        commit('CLEAR_USER');

        await dispatch('validate');

    },

    async updatePreferences({ dispatch }, generalPreferences) {
        const user = await axios.$put('/v1/accounts/preferences/general', {
            url_warning: generalPreferences.urlWarning,
            review_on_reply: generalPreferences.reviewOnReply,
            direct_email_to_network: generalPreferences.directEmailToNetwork
        });

        dispatch('saveAuthState', {
            user
        });

        return user;
    },

    get2FA() {
        return axios.$post('/v1/accounts/preferences/security/two_factor_authentication/setup');
    },

    async enable2FA({ dispatch, getters: { loggedUser } }, code) {
        await axios.$post('/v1/accounts/preferences/security/two_factor_authentication/activate', {
            otp_code: code
        });

        const updatedUser = cloneDeep({
            ...loggedUser,
            preferences: {
                ...loggedUser.preferences,
                security: {
                    twoFactorAuthorization: {
                        otpEnabled: true
                    }
                }
            }
        });

        dispatch('saveAuthState', { user: updatedUser });
    },

    update2FACodes() {
        return axios.$post('/v1/accounts/preferences/security/two_factor_authentication/regenerate_backup_codes');
    },

    saveAuthState({ commit }, { user, account, csrfToken, accessToken, refreshToken, isAccountOvertaken }) {
        const loggedUser = user?.id ? user : account;

        if (loggedUser !== undefined) {
            commit('SET_CURRENT_USER', loggedUser);

            // saving in window object because it is needed before computed are initialized
            window.globalDefaultVisibility = {
                entityType: 2,
                entityIds: [loggedUser?.businessProfile?.id || null]
            };
        }

        if (csrfToken !== undefined) {
            commit('SET_CSRF_TOKEN', csrfToken);
        }

        if (accessToken !== undefined) {
            commit('SET_ACCESS_TOKEN', accessToken);
        }

        if (refreshToken !== undefined) {
            commit('SET_REFRESH_TOKEN', refreshToken);
        }

        if (isAccountOvertaken !== undefined) {
            commit('SET_INVESTIGATING_ANOTHER_ACCOUNT', isAccountOvertaken);
        }
    },

    async updateDefaultStreams({ commit }, payload) {
        const res = await axios.$put('/v1/accounts/preferences/ui/default_streams', payload);

        commit('SET_DEFAULT_STREAMS', res.data);
    },

    async can({ getters: { permissions } }, { scope, permission, action }) {
        return permissions
            && permissions[scope]
            && permissions[scope][permission]
            && permissions[scope][permission][action] > 0;
    }
};


export default {state, getters, mutations, actions}
